import {ArtfactKind} from '@mrp-brasil/nick-data-commons';
import {BaseHook, Hook, HookClass, NickProcessArtfact} from '@mrp-brasil/nick-process-commons';
import {from, Observable, of} from 'rxjs';
import {concatMap, map, toArray} from 'rxjs/operators';

enum OperationKind {
    SUM = 0,
    SUB = 1,
    MULTI = 2,
    DIV = 3,
    PERCENT = 4,
    MOD = 5,
    WAVG = 6
}

@HookClass('math')
export class MathHook extends BaseHook {

    @Hook('75906b41-c9ad-492f-aab3-6f7fec34a7d4')
    public operations(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {

        return of(inputArtifact)
            .pipe(concatMap(() => {
                const outputArtifact = new NickProcessArtfact();
                outputArtifact.kind = ArtfactKind.ANY;
                outputArtifact.object = {};

                const accessField$ = (field: string, value: any) => {
                    const fields = field.split('.');
                    return fields.reduce((o: any, i: any) => (o ? o[i] : o), value);
                };

                if (!(inputArtifact.object.operations instanceof Array))
                    throw new Error('Operations must be a Array.');

                return from(inputArtifact.object.operations)
                    .pipe(map((operation: any) => {
                        if (!operation.values)
                            operation.values = [];

                        if (operation.values.length) {
                            switch (operation.kind) {
                                case OperationKind.SUM: {
                                    if (operation.field)
                                        operation.values = operation.values.map((value: any) => accessField$(operation.field, value));

                                    outputArtifact.object[operation.as] = operation.values.filter((v: any) => v != undefined).reduce((item: any, result: any) => {
                                        if (item == null)
                                            return parseFloat(result);

                                        if (item.startsWith && item.startsWith('...'))
                                            item = outputArtifact.object[item.replace('...', '')];

                                        if (result?.startsWith && result.startsWith('...'))
                                            result = outputArtifact.object[result.replace('...', '')];

                                        if (result == null) {
                                            return parseFloat(item);
                                        }

                                        return parseFloat(item) + parseFloat(result);
                                    });
                                    break;
                                }
                                case OperationKind.SUB: {
                                    if (operation.field)
                                        operation.values = operation.values.map((value: any) => accessField$(operation.field, value));

                                    outputArtifact.object[operation.as] = operation.values.filter((v: any) => v != undefined).reduce((item: any, result: any) => {
                                        if (item == null)
                                            return parseFloat(result);

                                        if (item?.startsWith && item.startsWith('...'))
                                            item = outputArtifact.object[item.replace('...', '')];

                                        if (result?.startsWith && result.startsWith('...'))
                                            result = outputArtifact.object[result.replace('...', '')];

                                        if (item == null)
                                            return 0;

                                        return parseFloat(item) - parseFloat(result);
                                    });
                                    break;
                                }
                                case OperationKind.MULTI:
                                    if (operation.field)
                                        operation.values = operation.values.map((value: any) => accessField$(operation.field, value));

                                    outputArtifact.object[operation.as] = operation.values.filter((v: any) => v != undefined).reduce((item: any, result: any) => {
                                        if (result == null || item == null)
                                            return 0;

                                        if (item.startsWith && item.startsWith('...'))
                                            item = outputArtifact.object[item.replace('...', '')];

                                        if (result?.startsWith && result.startsWith('...'))
                                            result = outputArtifact.object[result.replace('...', '')];

                                        return parseFloat(item) * parseFloat(result);
                                    });
                                    break;
                                case OperationKind.DIV:
                                    if (operation.field)
                                        operation.values = operation.values.map((value: any) => accessField$(operation.field, value));

                                    outputArtifact.object[operation.as] = operation.values.filter((v: any) => v != undefined).reduce((item: any, result: any) => {
                                        if (item == null)
                                            return 0;

                                        if (item.startsWith && item.startsWith('...'))
                                            item = outputArtifact.object[item.replace('...', '')];

                                        if (result?.startsWith && result.startsWith('...'))
                                            result = outputArtifact.object[result.replace('...', '')];

                                        return parseFloat(item) / parseFloat(result);
                                    });
                                    break;
                                case OperationKind.PERCENT: {
                                    if (operation.field)
                                        operation.values = operation.values.map((value: any) => accessField$(operation.field, value));

                                    outputArtifact.object[operation.as] = operation.values.filter((v: any) => v != undefined).reduce((item: any, result: any) => {
                                        if (item == null)
                                            return parseFloat(result);

                                        if (item.startsWith && item.startsWith('...'))
                                            item = outputArtifact.object[item.replace('...', '')];

                                        if (result?.startsWith && result.startsWith('...'))
                                            result = outputArtifact.object[result.replace('...', '')];

                                        if (item == null)
                                            return 0;

                                        return (parseFloat(item) / 100) * (parseFloat(result));
                                    });
                                    break;
                                }
                                case OperationKind.MOD: {
                                    if (operation.field)
                                        operation.values = operation.values.map((value: any) => accessField$(operation.field, value));

                                    outputArtifact.object[operation.as] = operation.values.filter((v: any) => v != undefined).reduce((item: any, result: any) => {
                                        if (item == null)
                                            return parseFloat(result);

                                        if (item.startsWith && item.startsWith('...'))
                                            item = outputArtifact.object[item.replace('...', '')];

                                        if (result?.startsWith && result.startsWith('...'))
                                            result = outputArtifact.object[result.replace('...', '')];

                                        if (item == null)
                                            return 0;

                                        return (parseFloat(item)) % (parseFloat(result));
                                    });
                                    break;
                                }
                                case OperationKind.WAVG: {
                                    const init = 0;
                                    if (operation.field) {
                                        operation.values = operation.values.map((value: any) => accessField$(operation.field, value));
                                        operation.weights = operation.weights.map((weights: any) => accessField$(operation.field, weights));
                                    }


                                    if (operation.values.length != operation.weights.length) {
                                        outputArtifact.object = {'Error': 'Arrays With Different Sizes'};
                                        break;
                                    }
                                    const weightValues = operation.values.filter((v: any) => v != undefined).reduce((accumulator: any, item: any, index: number) => {
                                        if (accumulator == null)
                                            return parseFloat(item);

                                        if (accumulator.startsWith && accumulator.startsWith('...'))
                                            accumulator = outputArtifact.object[accumulator.replace('...', '')];

                                        if (item?.startsWith && item.startsWith('...'))
                                            item = outputArtifact.object[item.replace('...', '')];

                                        if (accumulator == null)
                                            return 0;

                                        return parseFloat(item) * operation.weights[index] + parseFloat(accumulator);
                                    }, init);

                                    const sumWeights = operation.weights.filter((w: any) => w != undefined).reduce((accumulator: any, item: any) => {
                                        if (accumulator == null)
                                            return parseFloat(item);

                                        if (accumulator.startsWith && accumulator.startsWith('...'))
                                            accumulator = outputArtifact.object[accumulator.replace('...', '')];

                                        if (item?.startsWith && item.startsWith('...'))
                                            item = outputArtifact.object[item.replace('...', '')];

                                        if (accumulator == null)
                                            return 0;

                                        return (parseFloat(accumulator)) + (parseFloat(item));
                                    });
                                    outputArtifact.object[operation.as] = weightValues / sumWeights;
                                    break;
                                }

                            }
                        }
                        if (isNaN(outputArtifact.object[operation.as]))
                            outputArtifact.object[operation.as] = 0;

                    }), toArray())
                    .pipe(concatMap(() => {
                        return of(outputArtifact);
                    }));
            }));
    }

    @Hook('5810a282-dc7d-4b58-9466-d567783871dd')
    public randomNumber(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        return of(inputArtifact)
            .pipe(map((length: any) => {
                const artifact = new NickProcessArtfact();
                artifact.object = Math.floor(100000 + Math.random() * 900000).toString();
                artifact.kind = ArtfactKind.ANY;
                return artifact;
            }));
    }

    @Hook('e20ee773-71f2-44a7-8f3f-3717565a5c89')
    public randomNumberWithLength(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const artifact = new NickProcessArtfact();
        artifact.kind = ArtfactKind.ANY;

        let x = '';
        for (let i = 0; i < inputArtifact.object.config.length; i++) {
            x += Math.floor(Math.random() * 10);
        }

        artifact.object = x;
        return of(artifact);
    }

    @Hook('50712a44-e4c8-42f7-8f68-47083e9ce787')
    public toFixed(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        const artifact = new NickProcessArtfact();
        artifact.kind = ArtfactKind.ANY;
        artifact.object = parseFloat(inputArtifact.object.data).toFixed(inputArtifact.object.decimalPlaces);
        return of(artifact);
    }

    @Hook('faf24cc2-68b4-4291-ba85-b5f2aee608af')
    public mathApply(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
        let data = inputArtifact.object.data;

        const outputArtifact = new NickProcessArtfact();
        outputArtifact.kind = ArtfactKind.ANY;
        // @ts-ignore
        if (data instanceof Array) {
            // @ts-ignore
            outputArtifact.object = Math[inputArtifact.object.config.name](...data);
        } else {
            // @ts-ignore
            outputArtifact.object = Math[inputArtifact.object.config.name](data);
        }
        return of(outputArtifact);
    }

  @Hook('d027d1e2-30e8-41ba-bb4e-a55d32880dd7')
  public numberApply(inputArtifact: NickProcessArtfact): Observable<NickProcessArtfact> {
    let data = inputArtifact.object.data;

    const outputArtifact = new NickProcessArtfact();
    outputArtifact.kind = ArtfactKind.ANY;
    // @ts-ignore
    if (data instanceof Array) {
      // @ts-ignore
      outputArtifact.object = Number[inputArtifact.object.config.name](...data);
    } else {
      // @ts-ignore
      outputArtifact.object = Number[inputArtifact.object.config.name](data);
    }
    return of(outputArtifact);
  }
}
